import xml from 'highlight.js/lib/languages/xml'
import { lowlight } from 'lowlight/lib/core.js'
import ReactLowlight from 'react-lowlight'

import { logger } from './logger.js'

// Register one language by default to avoid warning
ReactLowlight.registerLanguage('html', xml)

export const highlightJsLanguages = [
  'arduino',
  'bash',
  'c',
  'cpp',
  'csharp',
  'css',
  'diff',
  'go',
  // 'graphql',
  'html',
  'ini',
  'java',
  'javascript',
  'json',
  'kotlin',
  'less',
  'lua',
  'makefile',
  'markdown',
  'objectivec',
  'perl',
  'php',
  'php-template',
  'plaintext',
  'python',
  'python-repl',
  'r',
  'ruby',
  'rust',
  'scss',
  'shell',
  'sql',
  'swift',
  'typescript',
  'vbnet',
  // 'wasm',
  'xml',
  'yaml',
] as const

type THighlightJsLanguages = (typeof highlightJsLanguages)[number]

const isSupportedLanguage = (
  language: string,
): language is THighlightJsLanguages =>
  highlightJsLanguages.includes(language as THighlightJsLanguages)

/**
 * dynamic import does not support import from module
 * https://github.com/rollup/plugins/tree/master/packages/dynamic-import-vars#imports-must-start-with--or-
 */
const resolveHighlightJsModule = (language: THighlightJsLanguages) => {
  switch (language) {
    case 'arduino':
      return import('highlight.js/lib/languages/arduino')
    case 'bash':
      return import('highlight.js/lib/languages/bash')
    case 'c':
      return import('highlight.js/lib/languages/c')
    case 'cpp':
      return import('highlight.js/lib/languages/cpp')
    case 'csharp':
      return import('highlight.js/lib/languages/csharp')
    case 'css':
      return import('highlight.js/lib/languages/css')
    case 'diff':
      return import('highlight.js/lib/languages/diff')
    case 'go':
      return import('highlight.js/lib/languages/go')
    case 'ini':
      return import('highlight.js/lib/languages/ini')
    case 'java':
      return import('highlight.js/lib/languages/java')
    case 'javascript':
      return import('highlight.js/lib/languages/javascript')
    case 'json':
      return import('highlight.js/lib/languages/json')
    case 'kotlin':
      return import('highlight.js/lib/languages/kotlin')
    case 'less':
      return import('highlight.js/lib/languages/less')
    case 'lua':
      return import('highlight.js/lib/languages/lua')
    case 'makefile':
      return import('highlight.js/lib/languages/makefile')
    case 'markdown':
      return import('highlight.js/lib/languages/markdown')
    case 'objectivec':
      return import('highlight.js/lib/languages/objectivec')
    case 'perl':
      return import('highlight.js/lib/languages/perl')
    case 'php':
      return import('highlight.js/lib/languages/php')
    case 'php-template':
      return import('highlight.js/lib/languages/php-template')
    case 'plaintext':
      return import('highlight.js/lib/languages/plaintext')
    case 'python':
      return import('highlight.js/lib/languages/python')
    case 'python-repl':
      return import('highlight.js/lib/languages/python-repl')
    case 'r':
      return import('highlight.js/lib/languages/r')
    case 'ruby':
      return import('highlight.js/lib/languages/ruby')
    case 'rust':
      return import('highlight.js/lib/languages/rust')
    case 'scss':
      return import('highlight.js/lib/languages/scss')
    case 'shell':
      return import('highlight.js/lib/languages/shell')
    case 'sql':
      return import('highlight.js/lib/languages/sql')
    case 'swift':
      return import('highlight.js/lib/languages/swift')
    case 'typescript':
      return import('highlight.js/lib/languages/typescript')
    case 'vbnet':
      return import('highlight.js/lib/languages/vbnet')
    case 'html':
      return import('highlight.js/lib/languages/xml')
    case 'xml':
      return import('highlight.js/lib/languages/xml')
    case 'yaml':
      return import('highlight.js/lib/languages/yaml')
    default: {
      // Code should never reach the default case
      const exhaustiveCheck: never = language
      return exhaustiveCheck
    }
  }
}

export const importHighlightJsLanguage = async (language: string) => {
  try {
    if (
      !language ||
      !isSupportedLanguage(language) ||
      lowlight.registered(language)
    ) {
      return
    }

    const module = await resolveHighlightJsModule(language)
    if (module) {
      ReactLowlight.registerLanguage(language, module.default)
    }
  } catch (e) {
    logger.warn('error loading highlight.js', language)
  }
}

export { lowlight }
